import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { useMe } from '@/lib/auth'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { primaryWallet } from '@/lib/api-types'
import { CUSTODY_LABELS, useCustody } from '@/lib/pool'
import { usePortfolio } from '@/lib/portfolio'
import { buildActivityRows, pricesFromHoldings } from '@/lib/activity'
import { useActivityHistory } from '@/lib/activity-history'
import {
  DATA_TYPES,
  RANGES,
  buildCsv,
  filterReportRows,
  rangeBounds,
  reportFileName,
  saveFile,
  toReportRows,
  tokenOptions,
  zoneInfo,
  type CustomRange,
  type DataType,
  type RangeKey,
} from '@/lib/reports'
import { Dropdown } from './dropdown'
import { TokenIcon } from './holdings-table'
import { Spinner } from './spinner'

const ALL_TOKENS = '__all__'

/**
 * Settings > Reports, laid out like Sonar's: pick the data type, the token
 * and the date range, then download the history as CSV or PDF. Everything
 * is in the viewer's own time zone and the files say which.
 */
export function ReportsSection() {
  const trpc = useTRPC()
  const { user } = useMe()
  const wallet = primaryWallet(user)
  const { exchange, custody } = useCustody()
  const custodyLabel = CUSTODY_LABELS[custody]
  const portfolio = usePortfolio(wallet)
  const strategies = useQuery(trpc.strategy.list.queryOptions())
  const history = useActivityHistory(wallet, exchange)
  const [dataType, setDataType] = useState<DataType>('all')
  const [token, setToken] = useState<string>(ALL_TOKENS)
  const [range, setRange] = useState<RangeKey>('all')
  const [custom, setCustom] = useState<CustomRange>({ from: '', to: '' })
  const [busy, setBusy] = useState<'pdf' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const holdings = useMemo(() => [...(portfolio.data?.tokens ?? []), ...(portfolio.data?.vault ?? [])], [portfolio.data])
  const prices = useMemo(() => pricesFromHoldings(holdings), [holdings])
  const rows = useMemo(
    () => (wallet && history.data ? buildActivityRows({ wallet, exchange, custodyLabel, prices, ...history.data }) : []),
    [wallet, exchange, custodyLabel, prices, history.data],
  )
  const strategyList = useMemo(() => strategies.data ?? [], [strategies.data])
  const reportRows = useMemo(() => toReportRows(rows, strategyList), [rows, strategyList])
  const tokens = useMemo(() => tokenOptions(rows, strategyList), [rows, strategyList])
  const bounds = rangeBounds(range, custom)
  const selectedToken = token === ALL_TOKENS ? null : token
  const filtered = useMemo(
    () => filterReportRows(reportRows, { dataType, token: selectedToken, from: bounds.from, to: bounds.to }),
    [reportRows, dataType, selectedToken, bounds.from, bounds.to],
  )
  const tokenLabel = tokens.find((t) => t.key === selectedToken)?.symbol ?? 'All tokens'
  const zone = zoneInfo()
  const loading = history.isPending || (strategies.isPending && !strategies.data)
  const ready = !loading && !history.isError && filtered.length > 0

  function downloadCsv() {
    setError(null)
    saveFile(buildCsv(filtered, zone), `${reportFileName(dataType)}.csv`, 'text/csv;charset=utf-8')
  }

  async function downloadPdf() {
    setError(null)
    setBusy('pdf')
    try {
      const { buildReportPdfFile } = await import('@/lib/report-pdf')
      const file = await buildReportPdfFile(filtered, { dataType, tokenLabel, rangeLabel: bounds.label, zone, generatedAt: new Date() }, reportFileName(dataType))
      saveFile(file.bytes, file.name, file.mime)
    } catch (err) {
      setError(errorMessage(err, 'The PDF could not be built.'))
    } finally {
      setBusy(null)
    }
  }

  const dataTypeOptions = DATA_TYPES.map((d) => ({ value: d.value, label: d.label }))
  const tokenChoices = [
    { value: ALL_TOKENS, label: 'All tokens' },
    ...tokens.map((t) => ({
      value: t.key,
      label: (
        <>
          <TokenIcon symbol={t.symbol} imageUrl={t.imageUrl} size="size-5" />
          <span>
            {t.symbol} <span className="text-muted">· Solana</span>
          </span>
        </>
      ),
    })),
  ]
  const rangeOptions = RANGES.map((r) => ({ value: r.value, label: r.label }))

  return (
    <section className="card">
      <div className="p-6">
        <h2 className="card-title">Reports</h2>
        <p className="mt-1 text-sm text-muted">Download transaction history.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Data type</span>
            <Dropdown value={dataType} options={dataTypeOptions} onChange={setDataType} label="Data type" />
          </div>
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Token</span>
            <Dropdown value={token} options={tokenChoices} onChange={setToken} label="Token" disabled={loading} />
          </div>
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Date range</span>
            <Dropdown value={range} options={rangeOptions} onChange={setRange} label="Date range" />
          </div>
        </div>
        {range === 'custom' ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="grid gap-1.5 text-sm">
              <span className="text-muted">From</span>
              <input type="date" className="control px-4 py-3 text-sm outline-none" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            </label>
            <label className="grid gap-1.5 text-sm">
              <span className="text-muted">To</span>
              <input type="date" className="control px-4 py-3 text-sm outline-none" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
            </label>
          </div>
        ) : null}
      </div>
      <div className="border-t border-line p-6">
        <p className="text-sm">Download:</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" className="btn btn-secondary" disabled={!ready || busy !== null} onClick={downloadCsv}>
            CSV <Download className="size-4" />
          </button>
          <button type="button" className="btn btn-secondary" disabled={!ready || busy !== null} onClick={() => void downloadPdf()}>
            {busy === 'pdf' ? <Spinner className="size-4" /> : <>PDF <Download className="size-4" /></>}
          </button>
          {loading ? (
            <span className="flex items-center gap-2 text-sm text-muted">
              <Spinner className="size-4" /> Loading your history
            </span>
          ) : history.isError ? (
            <span className="text-sm text-negative">
              {errorMessage(history.error, 'Your history could not be loaded.')}{' '}
              <button type="button" className="underline" onClick={() => void history.refetch()}>Retry</button>
            </span>
          ) : (
            <span className="text-sm text-muted">
              {filtered.length.toLocaleString('en-US')} {filtered.length === 1 ? 'record' : 'records'} · {bounds.label} · {zone.label}
            </span>
          )}
        </div>
        {error ? <p className="mt-3 text-sm text-negative">{error}</p> : null}
        <p className="mt-3 text-xs text-muted">Reports over 1,000 rows download as a ZIP of smaller PDFs.</p>
      </div>
    </section>
  )
}
